import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Checkbox } from '../../../../components/Checkbox';
import type {
  RecruitingStatus,
  RosterSettings,
  ScholarshipModel,
  ScholarshipPlayer,
  ScholarshipPlayerPosition,
} from '../../../../types/scholarships';
import {
  POSITION_GROUPS,
  type PositionGroup,
  positionLabel,
  positionToGroup,
  type RosterSport,
} from './positionGroups';
import { formatGroupAggregate, formatPlayerPercent } from './scholarshipPercent';
import { formatMoney } from './scholarshipMoney';
import { tourAnchor, type TourAnchorId } from '../../../tours/tourAnchors';
import { DragHandleIcon } from './icons';
import { HandednessName, IndicatorDots, TraitPill } from './RosterPlayerMarks';
import { PlayerScoutingLines } from './PlayerScoutingLines';
import {
  effectiveClass,
  effectiveClassLabel,
  isGraduationYearClass,
  isJucoClass,
} from './scholarshipClasses';
import { openableProspectId } from './openableProspect';

interface Props {
  players: ScholarshipPlayer[];
  scholarshipModel: ScholarshipModel;
  // Money column gating: NIL / Rev share columns follow the plan's tracking
  // toggles; Total Package shows when either is on.
  showNil: boolean;
  showRevShare: boolean;
  // Offered / Committed. Off for a scholarship-oriented roster, where the columns are noise.
  showRecruitingStatus: boolean;
  collapsedGroups: Set<PositionGroup>;
  onToggleGroup: (group: PositionGroup) => void;
  onEdit: (player: ScholarshipPlayer) => void;
  onRemove: (player: ScholarshipPlayer) => void;
  /**
   * Open the full player record behind an attached row. Only attached rows whose record still
   * exists get a clickable name — a manual row has no record to open, and a row flagged
   * "player removed" points at one that has been merged or deleted.
   */
  onOpenProspect?: (prospectId: string) => void;
  onRecruitingStatusChange: (player: ScholarshipPlayer, next: RecruitingStatus) => void;
  onReorder: (position: ScholarshipPlayerPosition, orderedIds: string[]) => void;
  sport: RosterSport;
  /** The board's lists, for the name colour, trait pill and indicator dots. */
  settings: RosterSettings;
}

const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
});

const formatCurrency = (n: number | null): string => (n == null ? '—' : currencyFormatter.format(n));
const formatText = (s: string | null): string => (s == null || s === '' ? '—' : s);
const formatUnit = (n: number | null): string => (n == null ? '—' : n.toFixed(1));

// Names whichever fact the Class cell is showing, or nothing when it is showing a dash.
const classCellTitle = (player: ScholarshipPlayer): string | undefined => {
  const shown = effectiveClass(player);
  if (shown == null) return undefined;
  if (isJucoClass(shown)) return 'Junior college year';
  return isGraduationYearClass(shown) ? 'Graduation year' : 'Year of eligibility';
};

// Order in which exact positions render inside a group's sub-blocks. Anything
// not listed here falls back to its raw string order (alphabetical).
const POSITION_RENDER_ORDER: ScholarshipPlayerPosition[] = [
  'RHP', 'LHP', 'C', '1B', '2B', '3B', 'SS', 'LF', 'CF', 'RF', 'DH', 'UTIL',
];

// Bucket players in their server-supplied order (no client-side resort) so
// drag-to-reorder reflects the canonical sort_order, not alphabetical.
const bucketByGroupThenPosition = (
  players: ScholarshipPlayer[],
): Map<PositionGroup, Map<ScholarshipPlayerPosition | null, ScholarshipPlayer[]>> => {
  const groups = new Map<PositionGroup, Map<ScholarshipPlayerPosition | null, ScholarshipPlayer[]>>();
  for (const group of POSITION_GROUPS) groups.set(group, new Map());
  for (const p of players) {
    const groupMap = groups.get(positionToGroup(p.position))!;
    const key: ScholarshipPlayerPosition | null = p.position ?? null;
    if (!groupMap.has(key)) groupMap.set(key, []);
    groupMap.get(key)!.push(p);
  }
  return groups;
};

const orderedPositions = (
  groupMap: Map<ScholarshipPlayerPosition | null, ScholarshipPlayer[]>,
): Array<ScholarshipPlayerPosition | null> => {
  const present = Array.from(groupMap.keys());
  return present.sort((a, b) => {
    if (a === null) return 1;
    if (b === null) return -1;
    const ia = POSITION_RENDER_ORDER.indexOf(a);
    const ib = POSITION_RENDER_ORDER.indexOf(b);
    if (ia === -1 && ib === -1) return a < b ? -1 : a > b ? 1 : 0;
    if (ia === -1) return 1;
    if (ib === -1) return -1;
    return ia - ib;
  });
};

/**
 * Which row carries each row-level tour anchor. Each goes on the first rendered row that has the
 * control, because not every row has every control: only attached rows open a player record, and
 * only rows with a position can be dragged.
 */
interface RowAnchorOwners {
  /** Edit, remove and recruiting status, which every row has. */
  any?: string;
  open?: string;
  drag?: string;
}

/** An attached row whose player record still exists, so its name opens that record. */
const canOpenProspect = (player: ScholarshipPlayer): boolean => openableProspectId(player) !== null;

const anchorIfOwner = (owner: string | undefined, player: ScholarshipPlayer, id: TourAnchorId) =>
  owner === player.id ? tourAnchor(id) : {};

/**
 * One Offered / Committed tick.
 *
 * A single stage rather than a free checkbox pair: the underlying field is one of
 * None | Offered | Committed, so ticking Committed implies an offer was made and unticking
 * Offered cannot leave a player committed-but-never-offered. Encoding that here keeps the two
 * columns the coach expects without letting them express a state the data cannot hold.
 *
 * Every row gets real checkboxes, attached or typed in by hand. A manual row's status is stored
 * on the row itself rather than on a player record it does not have — coaches track recruits
 * they typed in too, and a dash where a tick belonged read as the feature being broken.
 */
const RecruitingStatusCell = ({
  player,
  stage,
  onChange,
  anchorProps,
}: {
  player: ScholarshipPlayer;
  stage: Extract<RecruitingStatus, 'Offered' | 'Committed'>;
  onChange: (player: ScholarshipPlayer, next: RecruitingStatus) => void;
  /** Tour anchor, set on the first row's Offered cell only. */
  anchorProps?: { 'data-tour-anchor'?: TourAnchorId };
}) => {
  const status = player.recruitingStatus ?? 'None';
  // Committed implies offered, so the Offered box stays ticked at the later stage.
  const checked = stage === 'Offered'
    ? status === 'Offered' || status === 'Committed'
    : status === 'Committed';

  const handleChange = () => {
    if (stage === 'Offered') {
      // Clearing Offered clears everything — a player cannot be committed without an offer.
      onChange(player, checked ? 'None' : 'Offered');
    } else {
      // Clearing Committed steps back to Offered rather than to None: the offer was still made.
      onChange(player, checked ? 'Offered' : 'Committed');
    }
  };

  return (
    <td className="scholarship-players__cell--center" {...anchorProps}>
      <Checkbox
        checked={checked}
        onChange={handleChange}
        aria-label={`${stage} — ${player.firstName} ${player.lastName}`}
      />
    </td>
  );
};

interface RowProps {
  player: ScholarshipPlayer;
  /** Passed rather than read from context so this stays renderable without an AuthProvider. */
  sport: RosterSport;
  draggable: boolean;
  showUnits: boolean;
  showNil: boolean;
  showRevShare: boolean;
  showPackage: boolean;
  showScholarshipPercent: boolean;
  showRecruitingStatus: boolean;
  onEdit: (player: ScholarshipPlayer) => void;
  onRemove: (player: ScholarshipPlayer) => void;
  onOpenProspect?: (prospectId: string) => void;
  onRecruitingStatusChange: (player: ScholarshipPlayer, next: RecruitingStatus) => void;
  rowAnchorOwners: RowAnchorOwners;
  settings: RosterSettings;
}

const PlayerRow = ({
  player,
  sport,
  settings,
  draggable,
  showUnits,
  showNil,
  showRevShare,
  showPackage,
  showScholarshipPercent,
  showRecruitingStatus,
  onEdit,
  onRemove,
  onOpenProspect,
  onRecruitingStatusChange,
  rowAnchorOwners,
}: RowProps) => {
  const sortable = useSortable({ id: player.id, disabled: !draggable });
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = sortable;

  const style: React.CSSProperties = draggable
    ? {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.4 : 1,
      }
    : {};

  // Scholarship percent only when a cost makes it derivable; otherwise "—".
  // The column itself only renders for the percentage-based Equivalency model
  // (see showScholarshipPercent) — the dollar and Full/Half models have their
  // own measure.
  const pct = formatPlayerPercent(player);
  const totalPackage =
    (player.award ?? 0) + (player.nilAmount ?? 0) + (player.revShare ?? 0);
  const fullName = `${player.firstName} ${player.lastName}`;
  /*
    A linked row whose record is still there. `prospectMissing` rows keep a plain name: the
    record was merged away or deleted, so there is nothing behind the click.
  */
  const openableProspectId =
    onOpenProspect && canOpenProspect(player)
      ? player.prospectId
      : null;

  return (
    <tr ref={draggable ? setNodeRef : undefined} style={style}>
      {draggable && (
        <td className="scholarship-players__drag-cell">
          <button
            type="button"
            className="scholarship-players__drag-handle"
            {...anchorIfOwner(rowAnchorOwners.drag, player, 'rosters.row.drag')}
            aria-label={`Drag to reorder ${fullName}`}
            {...attributes}
            {...listeners}
          >
            <DragHandleIcon />
          </button>
        </td>
      )}
      <td title={fullName}>
        <span className="scholarship-players__name">
          {/* The same marks as the depth chart, pitching staff and lineups: dots, then the name in
              its handedness colour, then the trait. */}
          <IndicatorDots indicatorIds={player.indicatorIds} settings={settings} />
          {openableProspectId ? (
            <button
              type="button"
              className="scholarship-players__name-button"
              {...anchorIfOwner(rowAnchorOwners.open, player, 'rosters.row.open')}
              onClick={() => onOpenProspect!(openableProspectId)}
              aria-label={`View details for ${fullName}`}
            >
              <HandednessName name={fullName} bats={player.bats} settings={settings} />
            </button>
          ) : (
            <HandednessName name={fullName} bats={player.bats} settings={settings} />
          )}
          <TraitPill traitId={player.evaluationTraitId} settings={settings} />
          {player.prospectMissing && (
            /*
              Linked, but the player record is gone — merged away or deleted. Flagged rather
              than hidden: this row may carry an award, and dropping a funded roster spot
              silently is worse than showing a stale name the coach can act on.
            */
            <span
              className="scholarship-players__name-flag"
              title="This player record was merged or removed. The name shown is from when they were attached."
            >
              player removed
            </span>
          )}
        </span>
        <PlayerScoutingLines player={player} sport={sport} />
        {player.tags.length > 0 && (
          <span className="scholarship-players__tags">
            {player.tags.map((tag) => (
              <span
                key={tag.id}
                className="scholarship-players__tag"
                title={`${tag.tagName}: ${tag.tagValue}`}
              >
                {tag.tagValue}
              </span>
            ))}
          </span>
        )}
      </td>
      {/*
        Through positionLabel, like the group sub-header above it -- a raw position here read
        'UTIL' where the sub-header read 'UTL', and 'DH' where a softball board read 'DP'.
      */}
      <td>{player.position == null ? '—' : positionLabel(player.position, sport)}</td>
      {/*
        A graduation year for a linked row or for one where the coach typed a class year, years
        of eligibility otherwise. Two different facts under one header, which is why the cell
        carries a title rather than leaving a coach to guess whether "2027" is a class or a
        year. Read off the value shown rather than off whether the row is linked, so a typed
        year is named the same as an inherited one.
      */}
      <td title={classCellTitle(player)}>
        {effectiveClassLabel(player) ?? '—'}
      </td>
      <td>{formatText(player.bats)}/{formatText(player.throws)}</td>
      {showRecruitingStatus && (
        <>
          <RecruitingStatusCell
            player={player}
            stage="Offered"
            anchorProps={anchorIfOwner(rowAnchorOwners.any, player, 'rosters.row.status')}
            onChange={onRecruitingStatusChange}
          />
          <RecruitingStatusCell
            player={player}
            stage="Committed"
            onChange={onRecruitingStatusChange}
          />
        </>
      )}
      <td className="scholarship-players__cell--right">{formatCurrency(player.cost)}</td>
      <td className="scholarship-players__cell--right">{formatCurrency(player.award)}</td>
      {showUnits && (
        <td className="scholarship-players__cell--right">{formatUnit(player.scholarshipUnit)}</td>
      )}
      {showNil && (
        <td className="scholarship-players__cell--right">{formatCurrency(player.nilAmount)}</td>
      )}
      {showRevShare && (
        <td className="scholarship-players__cell--right">{formatCurrency(player.revShare)}</td>
      )}
      {showPackage && (
        <td className="scholarship-players__cell--right">{formatMoney(totalPackage)}</td>
      )}
      {showScholarshipPercent && (
        <td
          className={
            pct.isWalkOn
              ? 'scholarship-players__cell--right scholarship-players__percent scholarship-players__percent--walkon'
              : 'scholarship-players__cell--right scholarship-players__percent'
          }
          title={pct.isWalkOn ? 'Walk-on — no scholarship awarded' : undefined}
        >
          {pct.text}
        </td>
      )}
      <td className="scholarship-players__row-actions">
        <button
          type="button"
          className="scholarship-players__icon-button"
          {...anchorIfOwner(rowAnchorOwners.any, player, 'rosters.row.edit')}
          onClick={() => onEdit(player)}
          aria-label={`Edit ${fullName}`}
        >
          ✎
        </button>
        <button
          type="button"
          className="scholarship-players__icon-button scholarship-players__icon-button--danger"
          {...anchorIfOwner(rowAnchorOwners.any, player, 'rosters.row.remove')}
          onClick={() => onRemove(player)}
          aria-label={`Remove ${fullName}`}
        >
          ✕
        </button>
      </td>
    </tr>
  );
};

interface PositionTableProps {
  position: ScholarshipPlayerPosition | null;
  /** Passed rather than read from context so this stays renderable without an AuthProvider. */
  sport: RosterSport;
  players: ScholarshipPlayer[];
  showSubHeader: boolean;
  showUnits: boolean;
  showNil: boolean;
  showRevShare: boolean;
  showPackage: boolean;
  showScholarshipPercent: boolean;
  showRecruitingStatus: boolean;
  onEdit: (player: ScholarshipPlayer) => void;
  onRemove: (player: ScholarshipPlayer) => void;
  onOpenProspect?: (prospectId: string) => void;
  onRecruitingStatusChange: (player: ScholarshipPlayer, next: RecruitingStatus) => void;
  onReorder: (position: ScholarshipPlayerPosition, orderedIds: string[]) => void;
  rowAnchorOwners: RowAnchorOwners;
  settings: RosterSettings;
}

const PositionTable = ({
  position,
  settings,
  players,
  showSubHeader,
  showUnits,
  showNil,
  showRevShare,
  showPackage,
  showScholarshipPercent,
  showRecruitingStatus,
  onEdit,
  onRemove,
  onOpenProspect,
  onRecruitingStatusChange,
  onReorder,
  sport,
  rowAnchorOwners,
}: PositionTableProps) => {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const draggable = position != null;

  // Per-column widths in render order. Every column keeps its full width; the
  // table floors at their sum (`minWidth`) so the wrapper scrolls horizontally
  // — like the other tables in the app — rather than squeezing the Name column.
  // table-layout: fixed reads these from the colgroup, keeping columns aligned
  // across the per-position group tables.
  const columnWidths: number[] = [
    ...(draggable ? [40] : []),
    260, // Name, with the scouting lines under it
    80, // Position
    70, // Class
    60, // B/T
    ...(showRecruitingStatus ? [90, 100] : []), // Offered, Committed
    110, // Cost
    110, // Award
    ...(showUnits ? [90] : []),
    ...(showNil ? [110] : []),
    ...(showRevShare ? [120] : []),
    ...(showPackage ? [130] : []),
    ...(showScholarshipPercent ? [130] : []), // Scholarship %
    100, // Actions
  ];
  const minWidth = columnWidths.reduce((sum, w) => sum + w, 0);

  const handleDragEnd = (event: DragEndEvent) => {
    if (!draggable) return;
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = players.findIndex((p) => p.id === active.id);
    const newIndex = players.findIndex((p) => p.id === over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    const next = arrayMove(players, oldIndex, newIndex);
    onReorder(position, next.map((p) => p.id));
  };

  const table = (
    <>
      {showSubHeader && (
        <div className="scholarship-players__subgroup-header">
          {position == null ? 'Other' : positionLabel(position, sport)}{' '}
          <span className="scholarship-players__subgroup-count">({players.length})</span>
        </div>
      )}
      <table className="scholarship-players__table" style={{ minWidth: `${minWidth}px` }}>
        <colgroup>
          {columnWidths.map((w, i) => (
            <col key={i} style={{ width: `${w}px` }} />
          ))}
        </colgroup>
        <thead>
          <tr>
            {draggable && <th aria-label="Reorder" />}
            <th>Name</th>
            <th>Position</th>
            <th>Class</th>
            <th>B/T</th>
            {showRecruitingStatus && (
              <>
                <th className="scholarship-players__cell--center">Offered</th>
                <th className="scholarship-players__cell--center">Committed</th>
              </>
            )}
            <th className="scholarship-players__cell--right">Cost</th>
            <th className="scholarship-players__cell--right">Award</th>
            {showUnits && <th className="scholarship-players__cell--right">Units</th>}
            {showNil && <th className="scholarship-players__cell--right">NIL</th>}
            {showRevShare && <th className="scholarship-players__cell--right">Rev share</th>}
            {showPackage && <th className="scholarship-players__cell--right">Total package</th>}
            {showScholarshipPercent && (
              <th className="scholarship-players__cell--right">Scholarship %</th>
            )}
            <th aria-label="Actions" />
          </tr>
        </thead>
        <tbody>
          {players.map((p) => (
            <PlayerRow
              key={p.id}
              player={p}
              sport={sport}
              draggable={draggable}
              showUnits={showUnits}
              showNil={showNil}
              showRevShare={showRevShare}
              showPackage={showPackage}
              showScholarshipPercent={showScholarshipPercent}
              showRecruitingStatus={showRecruitingStatus}
              onEdit={onEdit}
              onRemove={onRemove}
              onOpenProspect={onOpenProspect}
              onRecruitingStatusChange={onRecruitingStatusChange}
              rowAnchorOwners={rowAnchorOwners}
              settings={settings}
            />
          ))}
        </tbody>
      </table>
    </>
  );

  if (!draggable) return table;

  return (
    <DndContext
      id={`players-${position}`}
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
    >
      <SortableContext items={players.map((p) => p.id)} strategy={verticalListSortingStrategy}>
        {table}
      </SortableContext>
    </DndContext>
  );
};

export const ScholarshipPlayersTable = ({
  players,
  scholarshipModel,
  showNil,
  showRevShare,
  showRecruitingStatus,
  collapsedGroups,
  onToggleGroup,
  onEdit,
  onRemove,
  onOpenProspect,
  onRecruitingStatusChange,
  onReorder,
  sport,
  settings,
}: Props) => {
  const buckets = bucketByGroupThenPosition(players);
  const showUnits = scholarshipModel === 'FullHalf';
  const showPackage = showNil || showRevShare;
  // Each model shows its own measure column: Full/Half → Units, Equivalency →
  // Scholarship %, the dollar model → neither (its dollars live in the money
  // columns). The group-header aggregate follows the same rule.
  const showScholarshipPercent = scholarshipModel === 'Equivalency';
  const showGroupAggregate = scholarshipModel !== 'Percentage';

  // Rows in the order they render, skipping collapsed groups, so a tour anchor never lands on a row
  // that is not on screen.
  const renderedPlayers = POSITION_GROUPS.flatMap((group) => {
    if (collapsedGroups.has(group)) return [];
    const groupMap = buckets.get(group)!;
    return orderedPositions(groupMap).flatMap((position) => groupMap.get(position)!);
  });
  const rowAnchorOwners: RowAnchorOwners = {
    any: renderedPlayers[0]?.id,
    open: onOpenProspect ? renderedPlayers.find(canOpenProspect)?.id : undefined,
    drag: renderedPlayers.find((player) => player.position != null)?.id,
  };

  if (players.length === 0) {
    return (
      <div className="scholarship-players__empty">
        <p>No players match the current filters.</p>
      </div>
    );
  }

  return (
    <div className="scholarship-players__groups">
      {POSITION_GROUPS.map((group) => {
        const groupMap = buckets.get(group)!;
        const groupTotal = Array.from(groupMap.values()).reduce((acc, arr) => acc + arr.length, 0);
        if (groupTotal === 0) return null;
        const collapsed = collapsedGroups.has(group);
        const headerId = `scholarship-players-group-${group}`;
        const allGroupPlayers = Array.from(groupMap.values()).flat();
        // FullHalf → "Units Σ", Equivalency → "Avg %". Hidden for the dollar
        // model, which has no single per-group measure (its money totals are in
        // the totals strip).
        const groupAvg = formatGroupAggregate(allGroupPlayers, scholarshipModel);
        const positions = orderedPositions(groupMap);
        const hasMultipleSubpositions = positions.length > 1;
        return (
          <section
            key={group}
            className="scholarship-players__group"
            aria-labelledby={headerId}
          >
            <h3 className="scholarship-players__group-header">
              <button
                id={headerId}
                type="button"
                className="scholarship-players__group-toggle"
                aria-expanded={!collapsed}
                onClick={() => onToggleGroup(group)}
              >
                <span className="scholarship-players__group-caret" aria-hidden="true">
                  {collapsed ? '▶' : '▼'}
                </span>
                <span className="scholarship-players__group-title">{group}</span>
                <span className="scholarship-players__group-count">({groupTotal})</span>
                {showGroupAggregate && (
                  <span
                    className={
                      groupAvg.isWalkOn
                        ? 'scholarship-players__group-avg scholarship-players__group-avg--walkon'
                        : 'scholarship-players__group-avg'
                    }
                    title={groupAvg.isWalkOn ? 'Group average — all players are walk-ons or have $0 awarded' : undefined}
                  >
                    {groupAvg.label} {groupAvg.text}
                  </span>
                )}
              </button>
            </h3>
            {!collapsed && positions.map((pos) => (
              <PositionTable
                key={pos ?? '__null__'}
                position={pos}
                sport={sport}
                players={groupMap.get(pos)!}
                showSubHeader={hasMultipleSubpositions}
                showUnits={showUnits}
                showNil={showNil}
                showRevShare={showRevShare}
                showPackage={showPackage}
                showScholarshipPercent={showScholarshipPercent}
                showRecruitingStatus={showRecruitingStatus}
                onEdit={onEdit}
                onRemove={onRemove}
                onOpenProspect={onOpenProspect}
                onRecruitingStatusChange={onRecruitingStatusChange}
                onReorder={onReorder}
                rowAnchorOwners={rowAnchorOwners}
                settings={settings}
              />
            ))}
          </section>
        );
      })}
    </div>
  );
};
